import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import JoinGuardianModal from '../Guardians/JoinGuardianModal';

/**
 * VedicBiophysicsBanner
 * ─────────────────────
 * The Sovereign Statement. The first truth a visitor witnesses.
 *
 * Full-width Miron-violet panel that establishes "VEDIC BIOPHYSICS"
 * as a physical fact, not fine print. Gold serif against deep violet.
 * Ornamental gold rules, sovereign mark, and a single italic tagline.
 *
 * Sits ABOVE the Hero. Scrolls with the page (not fixed). Clears the
 * 80px fixed header via pt-28.
 */
const VedicBiophysicsBanner = ({ testId = 'vedic-biophysics-banner' }) => {
  const [joinOpen, setJoinOpen] = useState(false);
  return (
  <section
    data-testid={testId}
    aria-label="The scientific pillar of the house — Vedic Biophysics"
    className="relative w-full overflow-hidden grain"
    style={{
      background:
        'radial-gradient(ellipse at 50% 30%, #2E1A4A 0%, #1E1230 55%, #150A22 100%)',
    }}
  >
    {/* Soft gold halo — quiet luxury, not loud */}
    <div
      aria-hidden
      className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[120%] h-[60%] opacity-[0.18] blur-3xl"
      style={{
        background:
          'radial-gradient(ellipse at center, rgba(212,175,90,0.55) 0%, transparent 70%)',
      }}
    />
    {/* Grain veil */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-30"
      style={{
        backgroundImage:
          'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '3px 3px',
      }}
    />

    <div
      className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20 md:pb-28 min-h-[88vh] flex flex-col justify-center items-center text-center"
    >
      {/* Sovereign mark */}
      <p
        data-testid={`${testId}-sovereign-mark`}
        className="font-mono text-[10px] md:text-[11px] tracking-[0.42em] uppercase text-[#D4AF5A] mb-8 md:mb-10 flex items-center gap-4"
      >
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
        The Sovereign Statement · § I
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
      </p>

      {/* The fact */}
      <h1
        data-testid={`${testId}-title`}
        className="font-serif font-light text-[#D4AF5A] leading-[0.88] tracking-[-0.01em]"
        style={{
          fontSize: 'clamp(56px, 12.5vw, 220px)',
          textShadow: '0 0 60px rgba(212,175,90,0.18)',
        }}
      >
        <span className="block">Vedic</span>
        <span
          className="block italic"
          style={{ color: '#E8C977' }}
        >
          Biophysics<span className="text-[#D4AF5A] not-italic">.</span>
        </span>
      </h1>

      {/* Tagline */}
      <p
        data-testid={`${testId}-tagline`}
        className="mt-10 md:mt-12 font-serif italic text-[#F4F8F9]/90 max-w-3xl leading-snug"
        style={{ fontSize: 'clamp(22px, 2.4vw, 38px)' }}
      >
        Radiance, distilled.
      </p>

      {/* Tri-rule witness */}
      <div className="mt-12 md:mt-16 flex items-center gap-6 md:gap-8">
        <span aria-hidden className="inline-block w-12 md:w-20 h-px bg-[#D4AF5A]/70" />
        <p
          data-testid={`${testId}-witness`}
          className="font-mono text-[10px] md:text-[11px] tracking-[0.38em] uppercase text-[#F4F8F9]/75"
        >
          The Scientific Pillar of the House
        </p>
        <span aria-hidden className="inline-block w-12 md:w-20 h-px bg-[#D4AF5A]/70" />
      </div>

      {/* Primary CTA — Join the Circle of Guardians */}
      <button
        type="button"
        onClick={() => setJoinOpen(true)}
        data-testid={`${testId}-join-cta`}
        className="group mt-10 md:mt-14 inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-[18px] font-mono text-[11px] md:text-[12px] tracking-[0.32em] uppercase font-semibold text-[#1A0F26] bg-[#D4AF5A] hover:bg-[#E8C977] transition-colors shadow-[0_22px_60px_-22px_rgba(212,175,90,0.65)]"
      >
        Join the Circle of Guardians
        <ArrowRight
          size={14}
          strokeWidth={1.8}
          className="transition-transform duration-500 group-hover:translate-x-0.5"
        />
      </button>
      <p
        data-testid={`${testId}-cta-caption`}
        className="mt-4 font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/55"
      >
        1,000 founding seats · 2× SEVA earn · named on the bottle
      </p>

      {/* Rhythmic pulse — the linguistic heartbeat of the house */}
      <p
        data-testid={`${testId}-pulse`}
        className="mt-10 md:mt-14 font-mono tracking-[0.48em] uppercase text-[#D4AF5A] font-semibold flex items-center gap-3 md:gap-5"
        style={{ fontSize: 'clamp(11px, 0.95vw, 15px)' }}
      >
        <span aria-hidden className="inline-block w-6 md:w-10 h-px bg-[#D4AF5A]/70" />
        Thorn · Steam · Shesha
        <span aria-hidden className="inline-block w-6 md:w-10 h-px bg-[#D4AF5A]/70" />
      </p>
      <p
        data-testid={`${testId}-pulse-coda`}
        className="mt-2 font-serif italic text-[#F4F8F9]/70"
        style={{ fontSize: 'clamp(13px, 1vw, 17px)' }}
      >
        The pulse of the Kewra.
      </p>

      {/* Scroll hint to the Hero — quiet, not loud */}
      <a
        href="#home-hero"
        data-testid={`${testId}-scroll-hint`}
        onClick={(e) => {
          e.preventDefault();
          document
            .querySelector('[data-testid="home-hero"], [data-testid="home-trinity"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        className="mt-10 md:mt-14 group inline-flex flex-col items-center gap-3 text-[#F4F8F9]/55 hover:text-[#D4AF5A] transition-colors duration-500"
      >
        <span className="font-mono text-[10px] tracking-[0.36em] uppercase">
          Witness the still
        </span>
        <span
          aria-hidden
          className="block w-px h-10 bg-current opacity-60 group-hover:h-14 transition-all duration-500"
        />
      </a>
    </div>

    {/* Top + bottom hairlines — a sovereign frame */}
    <span
      aria-hidden
      className="absolute top-0 left-0 right-0 h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.6) 40%, rgba(212,175,90,0.6) 60%, transparent)',
      }}
    />
    <span
      aria-hidden
      className="absolute bottom-0 left-0 right-0 h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.6) 40%, rgba(212,175,90,0.6) 60%, transparent)',
      }}
    />

    {/* Email-capture modal */}
    <JoinGuardianModal
      open={joinOpen}
      onClose={() => setJoinOpen(false)}
      testId={`${testId}-join-modal`}
      source="home_banner"
    />
  </section>
  );
};

export default VedicBiophysicsBanner;
